<template>
  <div class="widget-media-controls">
    <section  class="is-flex m-2 is-justify-content-space-between">
      <div class="is-flex">
        <button v-if="showTocDropdown && isDesktop"
                class="button is-primary toc-trigger"
                aria-haspopup="true"
                :class="{'toc-button-with-results': hasSearchResults}"
                @click.exact="toggleDesktopToc">
          <span class="icon">
            <i class="fas fa-lg fa-list-ol"/>
          </span>
          <span v-if="hasSearchResults" class="tag is-info">
            {{ tocResultCount }}
          </span>
        </button>
        <table-of-contents v-else
                           v-show="showTocDropdown"
                           :enable-three-d-content="enableThreeDContent"/>
        <widget-breadcrumbs v-show="!isLoading"
                            :breadcrumbs="displayBreadcrumbs"
                            @openAsset="handleRedirect"/>
      </div>
      <div class="buttons">
        <widget-toc-steppers v-show="showTocDropdown && !hasInfoPageOpen && !showThreeDContentBrowser"/>
      </div>
    </section>
    <widget-loading-icon v-if="isLoading"/>
    <widget-content-header v-else
                           :display-name="threeDContextTitle"
                           :hide-download="showThreeDContentBrowser"
                           :info-controls-enabled="showTocDropdown"/>

    <div class="widget-body">
      <toc-navigation v-if="showTocDropdown && isDesktop"
                      v-show="isTocOpen"
                      :enable-three-d-content="enableThreeDContent"
                      :query="tocQuery"/>
      <div class="widget-main">
        <three-d-content-browser v-if="showThreeDContentBrowser"/>
        <slot v-else/>
      </div>
    </div>
  </div>
</template>

<script setup>
import WidgetBreadcrumbs from '../components/WidgetBreadcrumbs'
import WidgetLoadingIcon from '@/components/widgets/components/WidgetLoadingIcon'
import TableOfContents from '@/components/library/content/header/TableOfContents'
import TocNavigation from '@/components/library/content/TocNavigation.vue'
import ThreeDContentBrowser from '@/components/library/content/ThreeDContentBrowser.vue'
import WidgetTocSteppers from '@/components/widgets/components/WidgetTocSteppers'
import WidgetContentHeader from '@/components/widgets/components/WidgetContentHeader'
import { toRaw, computed } from 'vue'
import { useBrowseFlows } from '@/components/widgets/composables/useBrowseFlows'
import { useFetch } from '@/components/widgets/composables/useFetchContent'
import { useRoute } from 'vue-router'
import { useSearch } from '@/components/widgets/composables/useSearch'
import { useStore } from 'vuex'
import { useWidgetNavigator } from '@/components/widgets/composables/useWidgetNavigator'
import { useInfoLandingPage } from '@/components/widgets/composables/useInfoLandingPage'
import { WidgetRootBreadcrumbs } from '@/interfaces/global'
import { useBreakpoints } from '@/helpers'
import { useShowNewAdmin } from '@/composables/useShowNewAdmin'

const {
  backToProductPage,
  breadcrumbs,
  goHome,
  isBook,
  navigateToContent
} = useWidgetNavigator()
const { hasInfoPageOpen } = useInfoLandingPage()
const { isLoading } = useFetch()
const { navigateToSearch } = useSearch()
const { goToLastBrowse } = useBrowseFlows()
const route = useRoute()
const store = useStore()
const { width } = useBreakpoints()
const { showNewAdmin } = useShowNewAdmin()

const filterQuery = computed(() => store.state.search.filters.query)
const enableThreeDContent = computed(() => showNewAdmin.value)
const hasThreeDContent = computed(() => {
  return enableThreeDContent.value
    && (store.state.content?.toc?.threeDModelSnapshots?.length ?? 0) > 0
})
const activeContent = computed(() => {
  const toc = store.state.content?.toc
  return toc?.viewerActiveContent || toc?.activeContent || '2d'
})
const selectedThreeDModel = computed(() => store.getters['content/selectedThreeDModel'])
const selectedThreeDSnapshot = computed(() => store.getters['content/selectedThreeDSnapshot'])
const showThreeDContentBrowser = computed(() => {
  return activeContent.value === '3d'
    && hasThreeDContent.value
    && isBook.value
    && !hasInfoPageOpen.value
})
const threeDContextTitle = computed(() => {
  if (!showThreeDContentBrowser.value) return ''

  return selectedThreeDSnapshot.value?.snapshotName
    || (selectedThreeDSnapshot.value?.id == null ? '' : String(selectedThreeDSnapshot.value.id))
    || selectedThreeDModel.value?.name
    || store.state.content?.toc?.book?.name
    || ''
})
const displayBreadcrumbs = computed(() => {
  if (!showThreeDContentBrowser.value) return breadcrumbs.value

  const book = store.state.content?.toc?.book
  const bookIndex = breadcrumbs.value.findIndex((crumb) => {
    return String(crumb?.id) === String(book?.id)
  })
  const crumbs = bookIndex >= 0
    ? breadcrumbs.value.slice(0, bookIndex + 1)
    : [...breadcrumbs.value]

  if (selectedThreeDModel.value) {
    crumbs.push({
      id: selectedThreeDModel.value.id,
      name: selectedThreeDModel.value.name,
      type: 'three-d-model'
    })
  }
  if (selectedThreeDSnapshot.value) {
    crumbs.push({
      id: selectedThreeDSnapshot.value.id,
      name: selectedThreeDSnapshot.value.snapshotName || String(selectedThreeDSnapshot.value.id),
      type: 'three-d-snapshot'
    })
  }
  return crumbs
})
const showToc = computed(() => {
  return store.getters['widgets/showToc']
})

//because of the layout and the fact that the TocNavigation panel is a separate component
//from the button that displays it in mobile view we need to pass in the logic to the widget content
//header to determine if we are in a view that should display it
const hasTocData = computed(() => {
  return Boolean(store.state.content?.toc?.book)
    && ((store.state.content?.toc?.items?.length ?? 0) > 0 || hasThreeDContent.value)
})

const showTocDropdown = computed(() => {
  return showToc.value && isBook.value && hasTocData.value && !isLoading.value
})

const isDesktop = computed(() => {
  return (width?.value ?? 0) >= 1024
})

const tocQuery = computed(() => {
  if (store.getters['widgets/isWidget']) {
    const stored = store.state.search?.filters?.tocQuery
    return (typeof stored === 'string' ? stored : '') || ''
  }
  const stored = store.state.search?.filters?.tocQuery
  if (typeof stored === 'string' && stored.length) return stored

  const q = route.query.q
  return (typeof q === 'string' ? q : '') || ''
})

const isTocOpen = computed(() => {
  return Boolean(store.state.content?.toc?.isTocOpen)
})

const tocResultCount = computed(() => {
  return Number(store.state.content?.toc?.resultCount || 0)
})

const hasSearchResults = computed(() => activeContent.value === '2d' && tocResultCount.value > 0)

function toggleDesktopToc () {
  store.dispatch('content/updateTocOpen', !isTocOpen.value)
}

function handleRedirect(dto) {
  if (showThreeDContentBrowser.value) {
    if (dto.type === 'three-d-model') {
      store.dispatch('content/selectThreeDModel', dto.id)
      return
    }
    if (String(dto.id) === String(store.state.content?.toc?.book?.id)) {
      store.dispatch('content/selectThreeDModel', null)
      return
    }
  }

  switch(dto.type) {
    case WidgetRootBreadcrumbs.browse:
      goToLastBrowse()
      break
    case WidgetRootBreadcrumbs.search:
      navigateToSearch(toRaw((filterQuery.value || route.query.q) ?? ''), {
        clearAllContent: true })
      break
    case WidgetRootBreadcrumbs.home:
      goHome()
      break
    case 'product-page':
      backToProductPage()
      break
    default:
      navigateToContent(toRaw(dto))
  }
}
</script>

<style scoped lang="scss">
.widget-media-controls {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1 1 auto;
  height: 100%;
  overflow: hidden;
}

.widget-media-controls > section {
  flex: 0 0 auto;
  flex-shrink: 0;
}

.widget-media-controls :deep(.library-content-header) {
  flex: 0 0 auto;
  flex-shrink: 0;
}

.widget-body {
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.widget-main {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: auto;
}

.toc-trigger .tag {
  margin-left: 0.25em;
}

.toc-button-with-results {
  padding-right: .25rem;
}
</style>
